<template>
  <section class="statistics-card">
    <h3 class="card-title">
      服务渠道统计
    </h3>
    <div
      ref="chartRef"
      class="channel-chart"
    />
  </section>
</template>

<script setup>
import { markRaw, nextTick, onBeforeUnmount, onMounted, useTemplateRef } from 'vue'
import * as echarts from 'echarts'
import { getServiceChannelStatistics } from '@/api/primaryChannelStatistics'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'

const CHANNEL_DEFINITIONS = [
  {
    code: ChannelIdentityTypeEnum.CITY_ASSOC.code,
    name: '直属事业单位及市级文艺家协会数量',
    color: '#6B4FF7'
  },
  {
    code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code,
    name: '区级文艺家协会数量',
    color: '#1478F2'
  },
  {
    code: ChannelIdentityTypeEnum.ART_NEW_ORG.code,
    name: '文艺两新组织数量',
    color: '#FFC51B'
  },
  {
    code: ChannelIdentityTypeEnum.SUPPORT_REGION.code,
    name: '对口帮扶地区组织数量',
    color: '#16C68B'
  }
]

const chartRef = useTemplateRef('chartRef')
let chart = null
let resizeObserver = null
let resizeFrame = 0
let totalCount = 0
let channelItems = buildDefaultItems()

function buildDefaultItems() {
  return CHANNEL_DEFINITIONS.map(item => ({
    ...item,
    value: 0,
    percentage: '0.00%'
  }))
}

function buildOption() {
  const mobile = (chart?.getWidth() || 0) <= 680
  const center = mobile ? ['50%', '36%'] : ['32%', '50%']
  return {
    color: channelItems.map(item => item.color),
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: '{b}<br/>{c} 个（{d}%）'
    },
    legend: {
      orient: 'vertical',
      left: mobile ? 'center' : '53%',
      bottom: mobile ? 0 : 'center',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: mobile ? 12 : 26,
      icon: 'circle',
      textStyle: {
        color: '#333',
        fontSize: mobile ? 12 : 15,
        rich: {
          name: { width: mobile ? 220 : 285 },
          count: { width: 38, align: 'right', fontWeight: 'bold' },
          unit: { width: 24, color: '#555' },
          rate: { width: 50, align: 'right', color: '#555' }
        }
      },
      formatter: (name) => {
        const item = channelItems.find(current => current.name === name)
        return `{name|${name}}{count|${item?.value || 0}}{unit| 个}{rate|${item?.percentage || '0.00%'}}`
      }
    },
    graphic: [{
      type: 'group',
      left: center[0],
      top: center[1],
      bounding: 'raw',
      children: [
        {
          type: 'text',
          y: -12,
          style: {
            text: String(totalCount),
            fill: '#222',
            font: 'bold 34px "Microsoft YaHei"',
            textAlign: 'center',
            textVerticalAlign: 'middle'
          }
        },
        {
          type: 'text',
          y: 22,
          style: {
            text: '子渠道总数',
            fill: '#999',
            font: '14px "Microsoft YaHei"',
            textAlign: 'center',
            textVerticalAlign: 'middle'
          }
        }
      ]
    }],
    series: [{
      name: '服务渠道统计',
      type: 'pie',
      center,
      radius: mobile ? [72, 110] : [82, 128],
      startAngle: 90,
      clockwise: true,
      label: { show: false },
      labelLine: { show: false },
      data: channelItems.map(item => ({
        name: item.name,
        value: item.value,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
            { offset: 0, color: item.color },
            { offset: 1, color: echarts.color.lift(item.color, 0.28) }
          ])
        }
      }))
    }]
  }
}

function renderChart() {
  chart?.setOption(buildOption(), { notMerge: true })
}

async function loadStatistics() {
  try {
    const response = await getServiceChannelStatistics()
    const payload = response && response.code === 200 ? (response.data || {}) : {}
    const sourceItems = Array.isArray(payload.items) ? payload.items : []
    totalCount = Number(payload.total) || 0
    channelItems = CHANNEL_DEFINITIONS.map((definition) => {
      const source = sourceItems.find(item => item.code === definition.code) || {}
      return {
        ...definition,
        name: source.name || definition.name,
        value: Number(source.value) || 0,
        percentage: source.percentage || '0.00%'
      }
    })
  } catch (error) {
    console.error('查询一级渠道服务渠道统计失败:', error)
    totalCount = 0
    channelItems = buildDefaultItems()
  }
  renderChart()
}

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadStatistics()
})

onBeforeUnmount(() => {
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 22px 24px 18px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.channel-chart {
  width: 100%;
  height: 360px;
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .channel-chart {
    height: 440px;
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .channel-chart {
    height: 420px;
  }
}
</style>
